// src/utils/downloadUtils.js

import { api } from "../../api/client";

/**
 * A published_url pointing at an external host (e.g. a Confluence page) is
 * a real absolute URL, not a path on this app's own backend. It needs no
 * auth of its own and isn't a discrete file — open it directly rather than
 * routing it through the authenticated-blob-fetch helpers below, which
 * would send this app's own bearer token cross-origin and get blocked by
 * CORS regardless.
 */
export const isExternalDocumentUrl = (url) => /^https?:\/\//i.test(url || "");

const filenameFromResponse = (url, response) => {
  let filename = url.split('/').pop();
  const disposition = response.headers['content-disposition'];
  if (disposition && disposition.indexOf('filename=') !== -1) {
    const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
    if (matches != null && matches[1]) {
      filename = matches[1].replace(/['"]/g, '');
    }
  } else {
    const contentType = response.headers['content-type'];
    if (contentType) {
      if (contentType.includes('application/pdf')) filename += '.pdf';
      else if (contentType.includes('markdown')) filename += '.md';
      else if (contentType.includes('wordprocessingml')) filename += '.docx';
      else if (contentType.includes('text/plain')) filename += '.txt';
    }
  }
  return decodeURIComponent(filename);
};

/**
 * Fetches a file through the axios client, so the Authorization bearer header
 * this app relies on is attached — a plain browser navigation to a protected
 * route would 403 without it.
 */
const fetchFile = async (url) => {
  if (!url) throw new Error("No URL provided");

  // If the backend returned a path starting with /api, strip it
  // so the Axios client's baseURL doesn't double it (e.g. /api/api/v1)
  const cleanUrl = url.replace(/^\/api/, "");

  const response = await api.get(cleanUrl, { responseType: "blob" });
  return { blob: response.data, filename: filenameFromResponse(url, response) };
};

/**
 * Fetches a file from a URL and forces the browser to download it.
 * @param {string} url - The URL of the file to download
 * @param {string} customFilename - Optional custom filename for the downloaded file
 */
export const downloadFileFromUrl = async (url, customFilename) => {
  const { blob, filename: fetchedFilename } = await fetchFile(url);
  const filename = customFilename || fetchedFilename;

  // Create a temporary local URL for the blob
  const blobUrl = window.URL.createObjectURL(blob);

  // Create a hidden <a> tag and click it programmatically
  const link = document.createElement("a");
  link.href = blobUrl;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};

/**
 * Fetches a file from a URL securely and returns a local object URL to be used by viewers.
 * It is the caller's responsibility to revoke the object URL via URL.revokeObjectURL
 * when it is no longer needed (e.g. component unmount).
 *
 * @param {string} url - The URL of the file to fetch
 * @returns {Promise<{blobUrl: string, filename: string}>} The local blob URL and its filename
 */
export const getBlobUrlFromFileUrl = async (url) => {
  const { blob, filename } = await fetchFile(url);
  return { blobUrl: window.URL.createObjectURL(blob), filename };
};